import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";
import Team from "@/components/Team";

const PRINCIPLES = [
  { t: "Direction before speed", d: "A fast campaign in the wrong direction is expensive noise. We fix the vector first." },
  { t: "Systems before campaigns", d: "Funnels, CRM and content architecture that keep working after the budget stops." },
  { t: "Proof before promises", d: "Every claim on this site maps to delivered work — pipelines grown 30%+, conversion 22% faster." },
];

const CAPABILITIES = [
  "Paid Media", "SEO & Content", "CRO", "Lead Scoring", "Salesforce CRM", "AI Automation",
  "Nurture Sequences", "Market Assessment", "Supply Chain Analysis", "Multichannel Retail", "B2B Pipelines", "Demand Generation",
];

export default function About() {
  return (
    <>
      <SEO
        title="About — Vector Growth and Advisory"
        description="Vector Growth and Advisory builds growth systems for European SMEs, tech distributors and enterprise brands — acquisition funnels, AI-CRM automation and market intelligence."
      />
      <section className="relative overflow-hidden pt-40 pb-16 md:pt-48" data-testid="about-hero">
        <div className="spot pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-[1440px] px-6 md:px-10">
          <Eyebrow>The advisory</Eyebrow>
          <h1 className="mt-6 max-w-4xl font-display text-4xl font-extrabold tracking-tight sm:text-6xl lg:text-7xl" data-testid="about-headline">
            The force behind <span className="text-gradient">the arrow.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Vector Growth and Advisory is a growth consultancy for European SMEs, tech
            distributors and enterprise brands. We don't run campaigns — we engineer the systems
            underneath them: acquisition funnels, AI-assisted Salesforce CRM pipelines and
            commercial market intelligence.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 py-16 md:px-10 md:py-24" data-testid="brand-block">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="clip-frame overflow-hidden border hairline bg-[#f3efe6] p-8 md:p-14" data-testid="brand-logo-frame">
              <img
                src="https://customer-assets-jai6qajn.emergentagent.net/job_4b11e1f3-1063-4368-b4f7-3f888f2ba10e/artifacts/e2ed93d0e839026e_Gemini_Generated_Image_c3u443c3u443c3u4.webp"
                alt="Vector Growth and Advisory logo"
                className="w-full mix-blend-multiply"
                data-testid="brand-logo-image"
              />
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500" data-testid="brand-logo-caption">
              The mark — a vector: direction, magnitude, momentum
            </p>
          </Reveal>

          <Reveal delay={0.12}>
            <Eyebrow>Operating principles</Eyebrow>
            <div className="mt-8 space-y-8">
              {PRINCIPLES.map((p, i) => (
                <div key={p.t} className="border-l border-azure/30 pl-6" data-testid={`principle-${i}`}>
                  <h3 className="font-display text-xl font-bold tracking-tight text-ink">{p.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{p.d}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-16">
          <div className="clip-frame relative overflow-hidden border hairline">
            <img
              src="https://images.unsplash.com/photo-1470075801209-17f9ec0cada6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxmdXR1cmlzdGljJTIwY29ycG9yYXRlJTIwYnVpbGRpbmclMjBhcmNoaXRlY3R1cmUlMjBtb2Rlcm4lMjBnbGFzcyUyMGR1c2t8ZW58MHx8fHwxNzkxNTU1NzM1fDA&ixlib=rb-4.1.0&q=85"
              alt="Modern European corporate architecture"
              className="h-[280px] w-full object-cover md:h-[360px]"
              data-testid="about-image-band"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
          </div>
        </Reveal>
      </section>

      <Team />

      <section className="border-t hairline bg-surface/30" data-testid="capabilities-block">
        <div className="mx-auto max-w-[1440px] px-6 py-20 md:px-10 md:py-28">
          <Reveal>
            <Eyebrow>Capabilities</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
              What the vector is made of.
            </h2>
            <div className="mt-10 flex flex-wrap gap-2.5" data-testid="capabilities-chips">
              {CAPABILITIES.map((c) => (
                <span key={c} className="rounded-full border hairline bg-elevated px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-600 transition-colors hover:border-neon/50 hover:text-neon">
                  {c}
                </span>
              ))}
            </div>
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="group mt-14 inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 font-mono text-xs uppercase tracking-[0.24em] text-ink transition-all duration-300 hover:shadow-[0_0_45px_rgba(47,168,224,0.45)]"
            >
              Work with Vector
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
